import { Link } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import {
  Button,
  ChevronLeftIcon,
  FileDownloadIcon,
  H2,
  H3,
  LoadingDots,
  Span,
  Text,
} from '@/components/atoms'
import { FileDocumentIcon } from '@/components/atoms/icons/file'
import {
  ActionModal,
  DocumentPreview,
  IdCard,
  InfoBanner,
  StickyFooter,
} from '@/components/molecules'
import { ROUTES } from '@/shared/constants/routes'
import { useClaimsDetailPage } from './page.claims-detail.hook'

export function ClaimsDetailPage() {
  const { t } = useTranslation()
  const {
    view,
    card,
    documents,
    isDocumentsLoading,
    downloadingId,
    hasDownloadError,
    handleDownload,
    documentPreview,
    statusClassName,
    canEdit,
    canSubmit,
    handleEdit,
    submitModal,
    handleOpenSubmit,
    isLoading,
    isNotFound,
  } = useClaimsDetailPage()

  return (
    <div className="flex min-h-dvh flex-col bg-white">
      <div className="mx-auto w-full max-w-266.5 px-6 pt-6 md:px-0">
        <Link
          to={ROUTES.CLAIMS}
          className="text-indigo-base flex w-fit items-center gap-1 text-sm"
          aria-label={t('claims.pet.back')}
          data-testid="claim-detail-back"
        >
          <ChevronLeftIcon />
        </Link>
      </div>

      {isLoading ? (
        <div className="flex flex-1 items-center justify-center">
          <LoadingDots className="loading-xl" />
        </div>
      ) : isNotFound || !view ? (
        <Text className="py-16 text-center text-neutral-600">
          {t('claims.detail.notFound')}
        </Text>
      ) : (
        <div className="mx-auto flex w-full max-w-266.5 flex-1 flex-col gap-8 px-6 pt-6 pb-8 md:flex-row md:px-0 md:pt-8 md:pb-32">
          <div className="flex flex-col gap-6 md:w-83.5 md:shrink-0">
            {card.petName && (
              <IdCard
                variant="with-photo"
                photoUrl={card.photoUrl}
                hidePhotoBackground={card.hidePhotoBackground}
                planName={card.petName}
                ownerName={card.ownerName}
                policyId={card.policyId}
                startDate={card.startDate}
                className="hidden max-w-83.5 md:flex"
              />
            )}
            <div className="flex flex-col gap-4">
              <H2 className="text-[2rem]! leading-10! md:text-[3.125rem]! md:leading-15!">
                <Span className="text-neutral-900" textSize="inherit">
                  {t('claims.detail.titleStart')}
                </Span>
                {view.claimNumber && (
                  <>
                    <br />
                    <Span className="text-indigo-base" textSize="inherit">
                      {view.claimNumber}
                    </Span>
                  </>
                )}
              </H2>
              <Span
                className={`tracking-base w-fit rounded-full px-3 py-1 text-xs! ${statusClassName}`}
              >
                {view.statusLabel}
              </Span>
            </div>
          </div>

          <div className="flex flex-1 flex-col gap-6">
            {view.missingFieldsMessage && (
              <InfoBanner message={view.missingFieldsMessage} />
            )}
            {view.amount && (
              <div className="flex flex-col gap-1 rounded-2xl border border-neutral-100 bg-(--file-upload-bg) p-6">
                <Text className="text-neutral-600">
                  {t('claims.detail.amount')}
                </Text>
                <Text className="text-indigo-base text-[2rem]! leading-10! font-bold">
                  {view.amount}
                </Text>
              </div>
            )}

            <div className="flex flex-col gap-4">
              <H3 className="text-[1.5rem] text-neutral-900">
                {t('claims.detail.detailsTitle')}
              </H3>
              <dl className="flex flex-col divide-y divide-neutral-100 rounded-2xl border border-neutral-100 bg-white">
                {view.rows.map((row) => (
                  <div
                    key={row.label}
                    className="flex items-center justify-between gap-6 px-5 py-4"
                  >
                    <Text as="dt" className="text-neutral-600">
                      {row.label}
                    </Text>
                    <Text as="dd" className="text-right text-neutral-900">
                      {row.value}
                    </Text>
                  </div>
                ))}
              </dl>
            </div>

            {view.description && (
              <div className="flex flex-col gap-3">
                <Text className="font-bold text-neutral-900">
                  {t('claims.detail.description')}
                </Text>
                <Text className="rounded-2xl border border-neutral-100 px-5 py-4 whitespace-pre-line text-neutral-900">
                  {view.description}
                </Text>
              </div>
            )}

            {(isDocumentsLoading || documents.length > 0) && (
              <div className="flex flex-col gap-3">
                <Text className="font-bold text-neutral-900">
                  {t('claims.detail.documents')}
                </Text>
                {isDocumentsLoading ? (
                  <LoadingDots />
                ) : (
                  <ul className="flex flex-col gap-2">
                    {documents.map((document) => (
                      <li
                        key={document.id}
                        className="flex items-center gap-3 rounded-lg border border-neutral-100 bg-white px-4 py-3"
                      >
                        <button
                          type="button"
                          onClick={() => documentPreview.openPreview(document)}
                          className="flex min-w-0 flex-1 items-center gap-3 text-left"
                          aria-label={t('claims.detail.preview')}
                          data-testid="claim-document-preview"
                        >
                          <FileDocumentIcon className="size-5 shrink-0 text-neutral-600" />
                          <span className="flex min-w-0 flex-1 flex-col">
                            <Span className="text-indigo-base truncate">
                              {document.file_name}
                            </Span>
                            {document.typeLabel && (
                              <Span className="text-xs! text-neutral-600">
                                {document.typeLabel}
                              </Span>
                            )}
                          </span>
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDownload(document)}
                          disabled={downloadingId === document.id}
                          aria-label={t('claims.detail.download')}
                          className="text-indigo-base disabled:text-neutral-300"
                          data-testid="claim-document-download"
                        >
                          {downloadingId === document.id ? (
                            <LoadingDots />
                          ) : (
                            <FileDownloadIcon className="size-5" />
                          )}
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
                {hasDownloadError && (
                  <Span className="text-error-base">
                    {t('claims.detail.downloadError')}
                  </Span>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {canEdit && (
        <StickyFooter>
          <Button
            variant={canSubmit ? 'secondary' : 'primary'}
            size="lg"
            sizeMobile="md"
            onClick={handleEdit}
            className={`h-12 md:w-auto ${canSubmit ? 'w-1/2 px-2! md:px-8!' : 'w-full'}`}
            data-testid="claim-detail-edit"
          >
            {t('claims.detail.edit')}
          </Button>
          {canSubmit && (
            <Button
              size="lg"
              sizeMobile="md"
              onClick={handleOpenSubmit}
              className="h-12 w-1/2 md:w-auto"
              data-testid="claim-detail-submit"
            >
              {t('claims.submit.confirm.submit')}
            </Button>
          )}
        </StickyFooter>
      )}

      <ActionModal
        isOpen={submitModal.isOpen}
        variant={submitModal.variant}
        title={submitModal.title}
        description={submitModal.description}
        primaryAction={submitModal.primaryAction}
        secondaryAction={submitModal.secondaryAction}
        onClose={submitModal.onClose}
      >
        {submitModal.errorMessage && (
          <Text className="text-error-base text-sm!">
            {submitModal.errorMessage}
          </Text>
        )}
      </ActionModal>
      <DocumentPreview
        isOpen={documentPreview.isOpen}
        fileName={documentPreview.fileName}
        contentType={documentPreview.contentType}
        kind={documentPreview.kind}
        objectUrl={documentPreview.objectUrl}
        isLoading={documentPreview.isLoading}
        hasError={documentPreview.hasError}
        canDownload={documentPreview.canDownload}
        onClose={documentPreview.onClose}
        onDownload={documentPreview.onDownload}
        onRenderError={documentPreview.onRenderError}
      />
    </div>
  )
}
